var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import moment from "moment";
import React, { useContext, useMemo, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import MediaQuery from "react-responsive";
import RouterLink from "react-router-dom/Link";
import { Box, Flex, Link, Text } from "rebass";
import Dropdown from "src/components/elements/Dropdown";
import Hamburger from "src/components/elements/Hamburger";
import { Container } from "src/components/utilities/Containers";
import { UserContext } from "src/context";
import { breakpoints, theme } from "src/styles/theme";
import { hasTeacherRole } from "src/utils/roles";
import styled from "styled-components";
import { AccountIcon, JokerIcon, LogoIcon, SearchIcon } from "./Icons";
import ScoreMeter from "./ScoreMeter";
import Searchbar from "./Searchbar";
import SearchbarMobile from "./SearchbarMobile";
var linkStyle = {
    fontSize: "1.2rem",
    color: "gray500",
    "&:hover:": {
        color: "secondary",
    },
};
var menuLinkStyle = {
    color: "text",
    fontSize: "1.4rem",
    fontWeight: "semibold",
    css: {
        position: "relative",
        textDecoration: "none",
        "&:hover::after": {
            content: '""',
            position: "absolute",
            right: 0,
            bottom: "-5px",
            left: 0,
            display: "block",
            height: "2px",
            backgroundColor: theme.colors.secondary,
        },
    },
};
var HeaderStyled = styled(Box)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  position: relative;\n  height: 4rem;\n  background-color: #ffffff;\n  box-shadow: 0 2px 4px 0 rgba(234, 234, 234, 0.5);\n\n  @media (min-width: ", ") {\n    height: 7rem;\n  }\n"], ["\n  position: relative;\n  height: 4rem;\n  background-color: #ffffff;\n  box-shadow: 0 2px 4px 0 rgba(234, 234, 234, 0.5);\n\n  @media (min-width: ", ") {\n    height: 7rem;\n  }\n"])), breakpoints.md);
var JokerContainer = styled(Box)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  position: relative;\n  width: 3.4rem;\n  height: 3.4rem;\n  border-radius: 50%;\n  background-color: transparent;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n\n  @media (min-width: ", ") {\n    outline: 8px solid #f5f5f5;\n  }\n"], ["\n  position: relative;\n  width: 3.4rem;\n  height: 3.4rem;\n  border-radius: 50%;\n  background-color: transparent;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n\n  @media (min-width: ", ") {\n    outline: 8px solid #f5f5f5;\n  }\n"])), breakpoints.md);
var LevelContainer = styled(Flex)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  padding: 0 1rem;\n  align-items: center;\n  gap: 0.6rem;\n  @media (min-width: ", ") {\n    gap: 2rem;\n  }\n"], ["\n  padding: 0 1rem;\n  align-items: center;\n  gap: 0.6rem;\n  @media (min-width: ", ") {\n    gap: 2rem;\n  }\n"])), breakpoints.md);
var Header = function (_a) {
    var authenticated = _a.authenticated, onMenuButtonClick = _a.onMenuButtonClick, open = _a.open;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    var user = useContext(UserContext);
    var _c = useState(false), searchMobileIsOpen = _c[0], setSearchMobileIsOpen = _c[1];
    var jokerRecoveryTooltip = useMemo(function () {
        if (!user ||
            user.jokerRestoreIntervalSeconds == null ||
            user.jokerRestoreIntervalSeconds <= 0) {
            return undefined;
        }
        var date = moment()
            .add(user.jokerRestoreIntervalSeconds, "seconds")
            .locale(i18n.language)
            .format(i18n.language.startsWith("fr") ? "D MMMM à HH:mm" : "MMMM D, LT");
        return t("header.jokerRecoveryTooltip", { date: date });
    }, [t, i18n.language, user === null || user === void 0 ? void 0 : user.jokerRestoreIntervalSeconds]);
    var toggleSearchbarMobile = function () {
        setSearchMobileIsOpen(!searchMobileIsOpen);
    };
    var isTeacher = hasTeacherRole(user);
    return (React.createElement(Box, { as: "header", color: "black", css: "line-height: 1;", className: "screen", id: "banner" }, authenticated ? (React.createElement(HeaderStyled, null,
        searchMobileIsOpen && (React.createElement(MediaQuery, { query: "(max-width: ".concat(breakpoints.mdPlus, ")") },
            React.createElement(SearchbarMobile, { toggleSearchbarMobile: toggleSearchbarMobile }))),
        React.createElement(Container, { css: "height: 100%; padding-top: 2rem;" },
            React.createElement(Flex, { mx: -2, alignItems: "center", justifyContent: "space-between", css: "height: 100%;" },
                React.createElement(Flex, { px: 2, alignItems: "center", css: "flex: 0 0 auto;" },
                    React.createElement(Hamburger, { onClick: onMenuButtonClick, open: open, "aria-label": open ? t("a11y.menu_collapse") : t("a11y.menu_expand"), "aria-controls": "drawer-menu", "aria-haspopup": "menu", "aria-expanded": open }),
                    React.createElement(RouterLink, { to: "/", "aria-label": t("a11y.home") },
                        React.createElement(LogoIcon, null))),
                React.createElement(MediaQuery, { query: "(min-width: ".concat(breakpoints.lg, ")") },
                    React.createElement(Flex, { px: 2, justifyContent: "center", css: "flex: 1 1 auto;" },
                        React.createElement(Searchbar, null))),
                React.createElement(Flex, { justifyContent: "flex-end", css: "flex: 0 0 auto;" },
                    React.createElement(LevelContainer, null,
                        !isTeacher && React.createElement(ScoreMeter, null),
                        !isTeacher && (React.createElement(JokerContainer, { title: jokerRecoveryTooltip },
                            React.createElement(Flex, { css: "width: 2.1rem; height: 2.1rem; justify-content: center; align-items: center;" },
                                React.createElement(JokerIcon, null)),
                            React.createElement(Text, { as: "span", color: "#5C5787", fontSize: "1.4rem", fontWeight: "bold" }, user.jokerCount))),
                        React.createElement(MediaQuery, { query: "(min-width: ".concat(breakpoints.lg, ")") },
                            React.createElement(Box, { css: "flex: 0 0 auto;" },
                                React.createElement(Dropdown, { label: isTeacher ? t("myAccount") : user.firstname, avatar: user.avatar, icon: React.createElement(AccountIcon, null), ariaOpen: t("a11y.close_account_dropdown"), ariaClose: t("a11y.open_account_dropdown") },
                                    React.createElement(Dropdown.Item, { to: "/mes-parametres", label: t("settings"), icon: React.createElement(Dropdown.SettingsIcon, null) }),
                                    isTeacher ? (React.createElement(Dropdown.Item, { to: "/mes-statistiques", css: "padding-top: .4rem;", label: t("statistics"), icon: React.createElement(Dropdown.BadgeIcon, null) })) : (React.createElement(Dropdown.Item, { to: "/mes-recompenses", css: "padding-top: .4rem;", label: t("awards"), icon: React.createElement(Dropdown.TrophyIcon, null) })),
                                    React.createElement(Dropdown.Item, { to: "/mes-contacts", css: "padding-top: .4rem;", label: t("addressBook"), icon: React.createElement(Dropdown.AddressBookIcon, null) }),
                                    React.createElement(MediaQuery, { maxWidth: breakpoints.lgPlus },
                                        React.createElement(Dropdown.Item, { css: "padding-top: .4rem;", href: "".concat(process.env.BASENAME, "logout"), label: t("logout"), icon: React.createElement(Dropdown.LogoutIcon, null) }))))),
                        React.createElement(MediaQuery, { minWidth: breakpoints.xl },
                            React.createElement(Flex, { alignItems: "center" },
                                React.createElement(Text, __assign({ as: "a" }, menuLinkStyle, { href: "".concat(process.env.BASENAME, "logout") }), t("logout")))),
                        React.createElement(MediaQuery, { query: "(max-width: ".concat(breakpoints.mdPlus, ")") },
                            React.createElement(Text, { color: "text" },
                                React.createElement(Flex, { as: "button", type: "button", onClick: toggleSearchbarMobile, alignItems: "center", justifyContent: "center", width: "4.4rem", p: 0, css: "height: 4.4rem; border: none; background-color: transparent; outline: 0;", "aria-label": t("a11y.open_search_bar") },
                                    React.createElement(SearchIcon, null)))))))))) : (React.createElement(React.Fragment, null,
        React.createElement(Box, { bg: "gray100" },
            React.createElement(Container, { css: "height: 100%;" },
                React.createElement(Flex, { mx: -2, py: ".5rem", alignItems: "center", justifyContent: "space-between", bg: "gray100" },
                    React.createElement(Text, { px: 2, fontSize: "1.2rem", as: "span" }, t("header.baseline")),
                    React.createElement(Box, { px: 2 },
                        React.createElement(Link, __assign({}, linkStyle), t("registration")),
                        "|",
                        React.createElement(Link, __assign({}, linkStyle), t("login")))))),
        React.createElement(HeaderStyled, null,
            React.createElement(Container, { css: "height: 100%;" },
                React.createElement(Flex, { alignItems: "center", justifyContent: "center", css: "height: 100%;" },
                    React.createElement(LogoIcon, null))))))));
};
export default withTranslation()(Header);
var templateObject_1, templateObject_2, templateObject_3;
